import { createContext, useCallback, useContext, useEffect, useMemo, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { Permission, Role } from "@digisubs/shared";
import { api, ApiError, getCurrentClientId, setAuthLostHandler, setCurrentClientId } from "@/lib/api";
import { disconnectSocket, joinClientRoom } from "@/lib/socket";

export interface MeClient {
  id: string;
  name: string;
  slug: string;
  logoUrl: string | null;
  currency: string;
  defaultMarket: string;
  timezone: string;
}

export interface MeOrganization {
  id: string;
  name: string;
  slug: string;
  logoUrl: string | null;
  status: string;
  role: Role;
  clients: MeClient[];
}

export interface Me {
  user: {
    id: string;
    email: string;
    name: string;
    avatarUrl: string | null;
    locale: string;
    emailVerified: boolean;
    twoFactorEnabled: boolean;
    isPlatformAdmin: boolean;
  };
  organizations: MeOrganization[];
  current: {
    orgId: string;
    orgName: string;
    clientId: string;
    clientName: string;
    currency: string;
    timezone: string;
    defaultMarket: string;
    role: Role;
    permissions: Permission[];
  } | null;
}

interface AuthValue {
  me: Me | null;
  loading: boolean;
  refresh: () => Promise<unknown>;
  switchClient: (clientId: string) => Promise<void>;
  logout: () => Promise<void>;
  can: (permission: Permission) => boolean;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ["me"],
    queryFn: async () => {
      try {
        return await api.get<Me>("/auth/me");
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    staleTime: 5 * 60_000,
    retry: false,
  });

  const me = query.data ?? null;

  useEffect(() => {
    setAuthLostHandler(() => {
      qc.setQueryData(["me"], null);
    });
  }, [qc]);

  useEffect(() => {
    if (me?.current) {
      if (getCurrentClientId() !== me.current.clientId) setCurrentClientId(me.current.clientId);
      joinClientRoom(me.current.clientId);
    }
  }, [me?.current?.clientId, me]);

  const switchClient = useCallback(
    async (clientId: string) => {
      await api.post("/auth/switch-client", { clientId }, { noClient: true });
      setCurrentClientId(clientId);
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== "me" });
      await qc.invalidateQueries({ queryKey: ["me"] });
      joinClientRoom(clientId);
    },
    [qc],
  );

  const logout = useCallback(async () => {
    await api.post("/auth/logout", undefined, { noClient: true }).catch(() => undefined);
    setCurrentClientId(null);
    disconnectSocket();
    await qc.cancelQueries();
    // Sign out first: setting "me" to null updates the mounted auth observer right away. qc.clear() before this
    // detached that observer from the cache, so it kept the old member and the login page bounced back to "/".
    qc.setQueryData(["me"], null);
    qc.removeQueries({ predicate: (q) => q.queryKey[0] !== "me" });
  }, [qc]);

  const value = useMemo<AuthValue>(
    () => ({
      me,
      loading: query.isLoading,
      refresh: () => query.refetch(),
      switchClient,
      logout,
      can: (permission) => Boolean(me?.current?.permissions.includes(permission)),
    }),
    [me, query, switchClient, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth outside AuthProvider");
  return ctx;
}

/** Current tenant scope; only call inside the authenticated shell. */
export function useScope() {
  const { me } = useAuth();
  if (!me?.current) throw new Error("No current client");
  return me.current;
}

export function useCan(permission: Permission): boolean {
  return useAuth().can(permission);
}
